import { App as AntApp, Alert, AutoComplete, Form, Input, Modal, Select } from "antd";
import { useEffect, useRef, useState } from "react";
import { ApiError } from "../api/client.js";
import { useSaveProperty, useTargets } from "../api/queries.js";
import { EDITOR_LANGUAGES, detectLanguage } from "../format.js";
import { ValueEditor } from "./ValueEditor.js";

interface CreatePropertyModalProps {
  application: string;
  open: boolean;
  onClose: () => void;
  onCreated: (name: string, target: string) => void;
}

export function CreatePropertyModal({ application, open, onClose, onCreated }: CreatePropertyModalProps) {
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<{ application: string; name: string; target: string; description?: string }>();
  const [value, setValue] = useState("");
  const [language, setLanguage] = useState("text");
  const [error, setError] = useState<string | null>(null);
  const languageTouched = useRef(false);
  const saveMutation = useSaveProperty();
  const targets = useTargets(application);

  // Fresh form every time the modal opens — cancelled attempts must not leak
  // values (especially a stale application) into the next one.
  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue({ application });
      setError(null);
      setValue("");
      setLanguage("text");
      languageTouched.current = false;
    }
  }, [open, application, form]);

  const submit = async () => {
    let fields: { application: string; name: string; target: string; description?: string };
    try {
      fields = await form.validateFields();
    } catch {
      return;
    }
    saveMutation.mutate(
      {
        application: fields.application,
        name: fields.name.trim(),
        target: fields.target.trim(),
        value,
        controlUpdateId: -1,
        description: fields.description?.trim() || undefined,
      },
      {
        onSuccess: (result) => {
          void message.success(`created — update #${result.updateId}`);
          setError(null);
          setValue("");
          form.resetFields();
          onCreated(fields.name.trim(), fields.target.trim());
        },
        onError: (caught) => {
          if (caught instanceof ApiError && caught.code === "already-exists") {
            setError("this property already has a value on that target — edit it instead");
          } else {
            setError(caught.message);
          }
        },
      },
    );
  };

  return (
    <Modal
      title="New property"
      open={open}
      onCancel={onClose}
      onOk={() => void submit()}
      okText="Create"
      confirmLoading={saveMutation.isPending}
      width={680}
      destroyOnHidden
    >
      {error && <Alert type="error" message={error} style={{ marginBottom: 12 }} showIcon />}
      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ application }}
      >
        <Form.Item
          label={<span className="microlabel">application</span>}
          name="application"
          rules={[{ required: true, message: "application required" }]}
        >
          <Input className="mono" placeholder="billing" />
        </Form.Item>
        <Form.Item
          label={<span className="microlabel">property name</span>}
          name="name"
          rules={[{ required: true, message: "name required" }]}
        >
          <Input className="mono" placeholder="db.pool.size" />
        </Form.Item>
        <Form.Item
          label={<span className="microlabel">target</span>}
          name="target"
          rules={[{ required: true, message: "target required" }]}
          extra="less specific targets are inherited — adigium://prod applies to everything under it"
        >
          <AutoComplete
            className="mono"
            placeholder="adigium://prod"
            options={(targets.data ?? []).map((target) => ({ value: target }))}
            filterOption={(input, option) =>
              (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
            }
          />
        </Form.Item>
        <Form.Item label={<span className="microlabel">value</span>}>
          <ValueEditor
            value={value}
            onChange={(next) => {
              setValue(next);
              if (!languageTouched.current) {
                setLanguage(detectLanguage(next));
              }
            }}
            language={language}
            height={180}
          />
          <Select
            size="small"
            value={language}
            onChange={(lang) => {
              languageTouched.current = true;
              setLanguage(lang);
            }}
            options={EDITOR_LANGUAGES.map((lang) => ({ value: lang, label: lang }))}
            style={{ width: 130, marginTop: 6 }}
          />
        </Form.Item>
        <Form.Item label={<span className="microlabel">description (optional)</span>} name="description">
          <Input.TextArea placeholder="what this property controls" maxLength={1000} autoSize={{ minRows: 1, maxRows: 4 }} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
